/**
 * @file `<CaptchaChallenge>` — fetches a PoW challenge from the
 *       daemon, hands it to a Web Worker, and calls `onSolved` with
 *       the resulting solution tuple.
 * @module @labcraft/www/ui/captcha
 *
 * Lifecycle (per mount):
 *   1. `GET /api/contact` → `{ nonce, difficulty, expiresAt, sig }`.
 *   2. Spawn the SHA-256 brute-force worker
 *      ([captcha_worker.ts](./captcha_worker.ts)); post the challenge.
 *   3. Worker iterates `counter` from 0 until
 *      `sha256(nonce || ':' || counter)` has ≥ `difficulty` leading
 *      zero bits, posts back `{ counter }`.
 *   4. Component invokes `props.onSolved(solution)` with the full
 *      challenge tuple + the discovered `counter`.
 *
 * Unmount terminates the worker (in case the loop is still running).
 * Errors (network failure, malformed response) surface in the UI but
 * don't block the form — the parent gates the submit button on
 * `solution != null`, so a never-solved challenge keeps the user from
 * submitting without spamming the operator log.
 *
 * Pattern reference: `useAsciinemaPlayer` in `landing.tsx` — same
 * lifecycle shape (construct in `useEffect`, dispose in cleanup,
 * StrictMode double-mount handled by the dispose).
 */

import { useEffect, useState, type JSX } from 'react'
import type { CaptchaSolution, ChallengeResponse } from '../shared/routes.ts'
import { IslandRuntime } from './islands/runtime.ts'
import styles from './captcha.module.css'

/**
 * Props for {@link CaptchaChallenge}.
 *
 * @property onSolved - Called once with the full solution tuple when
 *   the worker finds a satisfying `counter`. The parent stores the
 *   tuple and includes it in the `POST /api/contact` body.
 */
export interface CaptchaChallengeProps {
  onSolved: (solution: CaptchaSolution) => void
}

type Status =
  | { kind: 'fetching' }
  | { kind: 'solving'; elapsedMs: number }
  | { kind: 'solved' }
  | { kind: 'error'; message: string }

export function CaptchaChallenge(props: CaptchaChallengeProps): JSX.Element {
  const [status, setStatus] = useState<Status>({ kind: 'fetching' })

  useEffect(() => {
    let cancelled = false
    let worker: Worker | null = null
    let tickHandle: ReturnType<typeof setInterval> | null = null

    void (async () => {
      let challenge: ChallengeResponse
      try {
        challenge = await IslandRuntime.www().contact.fetchChallenge()
      } catch (err) {
        if (cancelled) return
        setStatus({
          kind: 'error',
          message: `Couldn't fetch challenge: ${(err as Error).message}`,
        })
        return
      }
      if (cancelled) return

      const startedAt = Date.now()
      setStatus({ kind: 'solving', elapsedMs: 0 })
      tickHandle = setInterval(() => {
        if (cancelled) return
        setStatus(prev =>
          prev.kind === 'solving'
            ? { kind: 'solving', elapsedMs: Date.now() - startedAt }
            : prev,
        )
      }, 100)

      // Dynamic `?worker` import — client-only. The static form would
      // be evaluated when the SSG's server render loads this module
      // (the contact form is server-rendered per ADR-026), and Worker
      // construction has no server-side meaning.
      const { default: CaptchaWorker } =
        await import('./captcha_worker.ts?worker')
      if (cancelled) return
      worker = new CaptchaWorker()
      worker.onmessage = (event: MessageEvent<{ counter: number }>) => {
        if (cancelled) return
        if (tickHandle != null) clearInterval(tickHandle)
        setStatus({ kind: 'solved' })
        props.onSolved({
          nonce: challenge.nonce,
          difficulty: challenge.difficulty,
          expiresAt: challenge.expiresAt,
          sig: challenge.sig,
          counter: event.data.counter,
        })
      }
      worker.onerror = ev => {
        if (cancelled) return
        if (tickHandle != null) clearInterval(tickHandle)
        setStatus({
          kind: 'error',
          message: `Worker error: ${ev.message}`,
        })
      }
      worker.postMessage({
        nonce: challenge.nonce,
        difficulty: challenge.difficulty,
      })
    })()

    return () => {
      cancelled = true
      if (tickHandle != null) clearInterval(tickHandle)
      if (worker != null) worker.terminate()
    }
    // Ignored deps: `props.onSolved` is captured at first run; if the
    // parent re-creates the callback identity, the existing worker is
    // still solving against a fresh challenge mid-flight. Keep the
    // effect single-shot per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={styles.widget}
      role="status"
      aria-live="polite"
      aria-atomic="true">
      {status.kind === 'fetching' && (
        <>
          <span className={styles.spinner} aria-hidden />
          <span className={styles.text}>Fetching challenge…</span>
        </>
      )}
      {status.kind === 'solving' && (
        <>
          <span className={styles.spinner} aria-hidden />
          <span className={styles.text}>
            Verifying you're human… {(status.elapsedMs / 1000).toFixed(1)}s
          </span>
        </>
      )}
      {status.kind === 'solved' && (
        <>
          <span className={styles.check} aria-hidden>
            ✓
          </span>
          <span className={styles.text}>Verified — ready to send</span>
        </>
      )}
      {status.kind === 'error' && (
        <span className={styles.error}>// {status.message}</span>
      )}
    </div>
  )
}
